import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { Alerta, ErrorApi, Pill } from '../components/ui';
import { CredencialesCR } from '../cr/CredencialesCR';
import { DocumentosCR } from '../cr/DocumentosCR';
import { EmisorCR } from '../cr/EmisorCR';
import { ProductosCR } from '../cr/ProductosCR';
import type { ConfiguracionCr, FacturacionCrApi } from '../cr/types';

type Tab = 'emisor' | 'credenciales' | 'productos' | 'documentos';

const PASOS: { clave: 'datosEmisor' | 'certificado' | 'credenciales' | 'activado'; texto: string; tab: Tab }[] = [
  { clave: 'datosEmisor', texto: 'Datos del emisor', tab: 'emisor' },
  { clave: 'certificado', texto: 'Llave criptográfica', tab: 'credenciales' },
  { clave: 'credenciales', texto: 'Credenciales ATV', tab: 'credenciales' },
  { clave: 'activado', texto: 'Activación del servicio', tab: 'credenciales' },
];

export function ConfiguracionFacturacionCostaRica({ api, idEmpresa }: { api: FacturacionCrApi; idEmpresa: number }) {
  const [config, setConfig] = useState<ConfiguracionCr | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tab, setTab] = useState<Tab>('emisor');
  const aplicar = useCallback((c: ConfiguracionCr) => setConfig(c), []);

  useEffect(() => {
    let vivo = true;
    setError(null);
    api
      .obtenerConfiguracion(idEmpresa)
      .then((c) => {
        if (!vivo) return;
        aplicar(c);
        const pendiente = PASOS.find((p) => p.clave !== 'activado' && !c.checklist[p.clave]);
        setTab(pendiente ? pendiente.tab : 'documentos');
      })
      .catch((e) => vivo && setError(e));
    return () => {
      vivo = false;
    };
  }, [api, idEmpresa, aplicar]);

  if (!config) {
    return <main className="pagina">{error ? <ErrorApi error={error} /> : <div className="cargando" aria-busy>Cargando configuración…</div>}</main>;
  }

  if (!config.habilitado) {
    return (
      <main className="pagina">
        <Encabezado config={config} />
        <Alerta tipo="aviso" titulo="Facturación electrónica no disponible">{config.motivoNoHabilitado}</Alerta>
      </main>
    );
  }

  const ck = config.checklist;
  const c = config.certificado;

  return (
    <main className="pagina">
      <Encabezado config={config}>
        {ck.listo ? <Pill tono="ok">Listo para emitir</Pill> : <Pill tono="pendiente">Configuración incompleta</Pill>}
      </Encabezado>

      <nav className="progreso" aria-label="Progreso de configuración">
        {PASOS.map((p, i) => (
          <button key={p.clave} type="button" className={'progreso__paso' + (ck[p.clave] ? ' hecho' : '') + (tab === p.tab ? ' en-tab' : '')} onClick={() => setTab(p.tab)}>
            <span className="progreso__num" aria-hidden>{ck[p.clave] ? '✓' : i + 1}</span>
            <span>{p.texto}</span>
          </button>
        ))}
      </nav>

      {c.estado === 'VENCIDO' && (
        <Alerta tipo="error" titulo="Su llave criptográfica venció">Sus comprobantes no se pueden firmar. Genere una nueva en ATV y cárguela en Certificado y Hacienda.</Alerta>
      )}
      {c.porVencer && c.estado !== 'VENCIDO' && (
        <Alerta tipo="aviso" titulo={`Su llave criptográfica vence en ${c.diasParaVencer} días`}>Genere una nueva en ATV y cárguela en Certificado y Hacienda.</Alerta>
      )}
      {!ck.activado && ck.datosEmisor && ck.certificado && ck.credenciales && (
        <Alerta tipo="info" titulo="Configuración completa">Solo falta que se active el servicio de facturación electrónica en su comercio. Si ya lo solicitó, quedará activo en breve.</Alerta>
      )}

      <div className="tabs" role="tablist">
        {(
          [
            ['emisor', 'Emisor'],
            ['credenciales', 'Certificado y Hacienda'],
            ['productos', 'Productos'],
            ['documentos', 'Comprobantes'],
          ] as const
        ).map(([t, texto]) => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'activo' : ''} onClick={() => setTab(t)}>{texto}</button>
        ))}
      </div>

      <div role="tabpanel">
        {tab === 'emisor' && <EmisorCR api={api} config={config} onGuardado={aplicar} />}
        {tab === 'credenciales' && <CredencialesCR api={api} config={config} onGuardado={aplicar} />}
        {tab === 'productos' && <ProductosCR api={api} idEmpresa={config.idEmpresa} />}
        {tab === 'documentos' && <DocumentosCR api={api} idEmpresa={config.idEmpresa} />}
      </div>
    </main>
  );
}

function Encabezado({ config, children }: { config: ConfiguracionCr; children?: ReactNode }) {
  return (
    <header className="pagina__cab">
      <div>
        <p className="eyebrow">Facturación electrónica · Costa Rica</p>
        <h1>{config.nombre}</h1>
      </div>
      <div className="pagina__cab-acciones">{children}</div>
    </header>
  );
}
